/**
 * Serviço de Anúncios Personalizados
 * 
 * Gerencia anúncios com base no perfil e comportamento do usuário,
 * similar ao Google Ads e Meta Ads.
 */

import { supabase } from '../lib/supabase';

export interface Advertisement {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  ctaText: string;
  ctaUrl: string;
  size: 'small' | 'medium' | 'large';
  variant: 'default' | 'premium';
  matchScore?: number;
}

/**
 * Busca anúncios personalizados para o usuário logado
 */
export async function getPersonalizedAds(limit: number = 3): Promise<Advertisement[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return getFallbackAds(limit);
    }

    // Chama função do Supabase que retorna anúncios personalizados
    const { data, error } = await supabase.rpc('get_personalized_ads', {
      p_user_id: user.id,
      p_limit: limit
    });

    if (error) {
      console.error('Erro ao buscar anúncios personalizados:', error);
      return getFallbackAds(limit);
    }

    if (!data || data.length === 0) {
      return getFallbackAds(limit);
    }

    return data.map((ad: any) => ({
      id: ad.ad_id,
      title: ad.title,
      description: ad.description,
      imageUrl: ad.image_url,
      ctaText: ad.cta_text,
      ctaUrl: ad.cta_url,
      size: ad.size,
      variant: ad.variant,
      matchScore: ad.match_score
    }));
  } catch (error) {
    console.error('Erro no serviço de anúncios:', error);
    return getFallbackAds(limit);
  }
}

/**
 * Registra impressão de anúncio
 */
export async function trackAdImpression(
  advertisementId: string,
  sessionId?: string,
  deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop'
): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;

    const { data, error } = await supabase.rpc('record_ad_impression', {
      p_advertisement_id: advertisementId,
      p_user_id: user.id,
      p_session_id: sessionId || generateSessionId(),
      p_device_type: deviceType
    });

    if (error) {
      console.error('Erro ao registrar impressão:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Erro ao rastrear impressão:', error);
    return null;
  }
}

/**
 * Registra clique em anúncio
 */
export async function trackAdClick(impressionId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('record_ad_click', {
      p_impression_id: impressionId
    });

    if (error) {
      console.error('Erro ao registrar clique:', error);
      return false;
    }

    return data === true;
  } catch (error) {
    console.error('Erro ao rastrear clique:', error);
    return false;
  }
}

/**
 * Gera ID de sessão único
 */
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Detecta tipo de dispositivo
 */
export function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Anúncios de fallback (genéricos)
 */
function getFallbackAds(limit: number): Advertisement[] {
  const ads: Advertisement[] = [
    {
      id: 'fallback-1',
      title: 'Invista com Segurança',
      description: 'Abra sua conta em uma corretora 100% digital e comece a investir com taxa zero.',
      ctaText: 'Criar conta grátis',
      ctaUrl: 'https://www.clear.com.br',
      size: 'large',
      variant: 'premium'
    },
    {
      id: 'fallback-2',
      title: 'Educação Financeira Gratuita',
      description: 'Acesse cursos completos sobre investimentos, planejamento e muito mais.',
      ctaText: 'Começar agora',
      ctaUrl: 'https://www.b3.com.br',
      size: 'medium',
      variant: 'default'
    },
    {
      id: 'fallback-3',
      title: 'Comparador de Investimentos',
      description: 'Compare rentabilidade de diferentes ativos e encontre a melhor opção para você.',
      ctaText: 'Comparar agora',
      ctaUrl: 'https://www.investidor10.com.br',
      size: 'medium',
      variant: 'default'
    },
    {
      id: 'fallback-4',
      title: 'Consultoria Financeira',
      description: 'Planeje seu futuro com ajuda de especialistas certificados em finanças pessoais.',
      ctaText: 'Agendar consulta',
      ctaUrl: 'https://www.planejar.org.br',
      size: 'medium',
      variant: 'default'
    }
  ];

  return ads.slice(0, limit);
}

/**
 * Atualiza preferências de anúncios do usuário
 */
export async function updateAdPreferences(preferences: {
  allowPersonalized?: boolean;
  blockedAdvertisers?: string[];
  interestedCategories?: string[];
}): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return false;

    const { error } = await supabase
      .from('user_ad_preferences')
      .upsert({
        user_id: user.id,
        allow_personalized_ads: preferences.allowPersonalized,
        blocked_advertisers: preferences.blockedAdvertisers,
        interested_categories: preferences.interestedCategories,
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Erro ao atualizar preferências:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Erro ao salvar preferências:', error);
    return false;
  }
}
